/**
 * The body of the reservation contact form (BON-472): what changing the address
 * will cause, the two fields, and whatever went wrong with the last attempt.
 *
 * THE WARNING IS UNCONDITIONAL, and that is the point. It describes what
 * changing an address does; it does not claim this save will do it. Showing it
 * only when the field looks different would mean comparing addresses in the
 * browser, which is exactly what rotates nothing and warns anyway on a case-only
 * retype — the server compares trimmed and lowercased and is the only thing that
 * knows.
 *
 * IT SAYS "PAYMENT LINK", NOT "BOOKING LINK", and it says nothing about the old
 * one dying. A CSR has never seen either link and cannot check one; the only
 * part of this they can act on is that changing the address puts a new payment
 * link in front of the customer.
 */
import { LabeledTextInput } from '../../shared/LabeledTextInput';

export function ReservationContactFields({
  name,
  email,
  onNameChange,
  onEmailChange,
  error,
  nameRef,
}: {
  name: string;
  email: string;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  /** Whatever stopped the last save — a validation message or the server's own
   *  refusal. Every failure lands here rather than closing the form, so a
   *  corrected address is never thrown away. */
  error: string | null;
  nameRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="space-y-4 px-6 py-5">
      <div
        id="edit-reservation-contact-warning"
        className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
      >
        If the email address changes, we’ll need to send a new payment link to the customer.
      </div>

      <LabeledTextInput
        id="reservation-contact-name"
        label="Name"
        required
        maxLength={200}
        value={name}
        onChange={onNameChange}
        inputRef={nameRef}
      />
      <LabeledTextInput
        id="reservation-contact-email"
        label="Email"
        type="email"
        required
        maxLength={320}
        value={email}
        onChange={onEmailChange}
      />
      {/* `alert` so the failure is announced rather than left for a screen-reader
          user to go looking for after pressing Save. */}
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
